import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

export enum EditButtonStates {
  NOT_EDITING = 'NOT_EDITING',
  EDITING = 'EDITING',
  SAVING = 'SAVING',
}

export const DEFAULT_EDIT_BUTTON_STYLES = {
  cancelButton: ButtonStyle.TERTIARY_GRAY,
  doneButton: ButtonStyle.TERTIARY_BLUE,
  editButton: ButtonStyle.TERTIARY_GRAY,
  savingButton: ButtonStyle.TERTIARY_BLUE,
};

export interface ButtonStyles {
  cancelButton: ButtonStyle;
  doneButton: ButtonStyle;
  editButton: ButtonStyle;
  savingButton: ButtonStyle;
}

export interface EditButtonProps {
  buttonStyles: ButtonStyles;
  editingState: EditButtonStates;
  saveDisabled: boolean;
  editDisabled: boolean;
  onCancel: () => void;
  onComplete: () => void;
  onEdit: () => void;
}

/*
 * In read mode, shows an Edit button that calls `onEdit` when clicked. If `editDisabled` is true, the Edit
 * button is disabled.
 * In edit mode, shows Cancel and Done buttons that call `onCancel` and `onComplete` respectively
 * when clicked. If `saveDisabled` is true, the Done button is disabled.
 * In saving mode, shows a disabled "Saving..." button.
 */
export default function EditButtons(props: EditButtonProps): JSX.Element {
  switch (props.editingState) {
    case EditButtonStates.EDITING:
      return (
        <div>
          <Button
            buttonStyle={props.buttonStyles.cancelButton}
            onClick={props.onCancel}
            size={ButtonSize.MINI}
            text="Cancel"
          />
          <Button
            buttonStyle={props.buttonStyles.doneButton}
            className="u-mini-spacing-left"
            disabled={props.saveDisabled}
            onClick={props.onComplete}
            size={ButtonSize.MINI}
            text="Done"
            dataTestAttrs={{component: 'EditButtons', element: 'doneButton'}}
          />
        </div>
      );
    case EditButtonStates.NOT_EDITING:
      return (
        <Button
          buttonStyle={props.buttonStyles.editButton}
          disabled={props.editDisabled}
          onClick={props.onEdit}
          size={ButtonSize.MINI}
          text="Edit"
          dataTestAttrs={{component: 'EditButtons', element: 'editButton'}}
        />
      );
    case EditButtonStates.SAVING:
      return (
        <Button
          buttonStyle={props.buttonStyles.savingButton}
          disabled={true}
          size={ButtonSize.MINI}
          text="Saving..."
        />
      );
    default:
      throw new Error(`Invalid edit button state ${props.editingState}`);
  }
}

EditButtons.propTypes = {
  buttonStyles: PropTypes.shape({
    cancelButton: PropTypes.string.isRequired,
    doneButton: PropTypes.string.isRequired,
    editButton: PropTypes.string.isRequired,
    savingButton: PropTypes.string.isRequired,
  }).isRequired,
  // eslint-disable-next-line no-restricted-syntax
  editingState: PropTypes.oneOf(_.values(EditButtonStates)).isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  editDisabled: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

EditButtons.defaultProps = {
  saveDisabled: false,
  editDisabled: false,
  buttonStyles: DEFAULT_EDIT_BUTTON_STYLES,
};
